/**
 * React context that holds the editor state (selected node, document, etc.)
 * and actions (add, move, resize, update, delete nodes).
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  ComponentNode,
  ContainerNode,
  InjectedData,
  LayoutDocument,
  LayoutListComponentProps,
  LayoutNode,
} from "../layout-types";
import type { BarLoaderProps } from "../components/barLoaderConfig";
import { syncBarLoaderDiskStylesToNodeBounds } from "../components/barLoaderConfig";
import { getMockLayoutData } from "../layout-types";
import {
  cloneNodeWithFreshIds,
  normalizeNodeForContainerParent,
  normalizePastedNodesForParentLayout,
} from "./nodeClone";
function asPlainObject(value: unknown): Record<string, unknown> {
  if (value != null && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
}

/**
 * True only for plain object literals (not arrays, Date, Prisma.Decimal, etc.).
 * Required for mock merge: Decimal instances expose enumerable internal keys (s, e, d);
 * treating them as plain objects corrupts values into `{ s, e, d }` shapes.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function isArrayIndexSegment(segment: string): boolean {
  return /^[0-9]+$/.test(segment);
}

function cloneMockValue<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => cloneMockValue(item)) as T;
  }
  if (isPlainObject(value)) {
    const cloned: Record<string, unknown> = {};
    for (const [key, nestedValue] of Object.entries(value)) {
      cloned[key] = cloneMockValue(nestedValue);
    }
    return cloned as T;
  }
  return value;
}

function deepMergeMockValue(base: unknown, override: unknown): unknown {
  if (override === undefined) return cloneMockValue(base);
  if (base === undefined) return cloneMockValue(override);

  if (Array.isArray(base) && Array.isArray(override)) {
    const maxLength = Math.max(base.length, override.length);
    const merged: unknown[] = [];
    for (let index = 0; index < maxLength; index += 1) {
      merged[index] = deepMergeMockValue(base[index], override[index]);
    }
    return merged;
  }

  if (isPlainObject(base) && isPlainObject(override)) {
    const keys = new Set([...Object.keys(base), ...Object.keys(override)]);
    const merged: Record<string, unknown> = {};
    for (const key of keys) {
      merged[key] = deepMergeMockValue(base[key], override[key]);
    }
    return merged;
  }

  return cloneMockValue(override);
}

function setMockValueAtPath(
  current: unknown,
  segments: string[],
  value: unknown,
): unknown {
  if (segments.length === 0) return value;
  const [segment, ...rest] = segments;
  if (!segment) return current;

  if (isArrayIndexSegment(segment)) {
    const index = Number(segment);
    const sourceArray = Array.isArray(current) ? current : [];
    const nextArray = [...sourceArray];
    nextArray[index] = setMockValueAtPath(nextArray[index], rest, value);
    return nextArray;
  }

  const sourceObject = isPlainObject(current)
    ? current
    : ({} as Record<string, unknown>);
  const nextObject: Record<string, unknown> = { ...sourceObject };
  nextObject[segment] = setMockValueAtPath(nextObject[segment], rest, value);
  return nextObject;
}

import { addNodeDefaultValues } from "../../../../util/src/layout";
import {
  allocateNameForNode,
  allocateNamesForTree,
  ensureLayoutNodeNames,
} from "../../../../util/src/layout/node-display-label.js";
import {
  collectReferencedSceneCatalog,
  mergeSceneCatalogs,
  sceneCatalogHasEntries,
} from "../../../../util/src/layout/scene-catalog-clipboard.js";
import type { SceneBehaviorCatalog } from "../layout-types";
import type { LayoutClipboardPayload } from "./editorClipboard";

/** Max undo steps kept in memory (full document snapshots). */
const MAX_UNDO_STACK = 80;

/** Default delay to merge rapid property / behavior edits into one undo step. */
const DEBOUNCE_HISTORY_MS = 300;

function cloneLayoutDocument(doc: LayoutDocument): LayoutDocument {
  return structuredClone(doc);
}

function layoutDocumentsEqual(a: LayoutDocument, b: LayoutDocument): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export type UpdateNodeHistoryOption = "immediate" | "debounced";

function resolveEditableLayoutListTemplate(
  props: Partial<LayoutListComponentProps>,
): ContainerNode | undefined {
  if (!props.rowTemplate) return undefined;
  const selectedVariantName = props.editRowVariantName;
  if (!selectedVariantName) return props.rowTemplate;
  const selectedVariant = (props.rowVariants ?? []).find(
    (variant) => variant.name === selectedVariantName,
  );
  return selectedVariant?.template ?? props.rowTemplate;
}

function applyEditableLayoutListTemplate(
  props: LayoutListComponentProps,
  nextTemplate: ContainerNode,
): LayoutListComponentProps {
  const selectedVariantName = props.editRowVariantName;
  if (!selectedVariantName) {
    return {
      ...props,
      rowTemplate: nextTemplate,
    };
  }
  const hasVariant = (props.rowVariants ?? []).some(
    (variant) => variant.name === selectedVariantName,
  );
  if (!hasVariant) {
    const seededTemplate = cloneNodeWithFreshIds(nextTemplate) as ContainerNode;
    return {
      ...props,
      rowVariants: [
        ...(props.rowVariants ?? []),
        {
          name: selectedVariantName,
          template: seededTemplate,
        },
      ],
    };
  }
  return {
    ...props,
    rowVariants: (props.rowVariants ?? []).map((variant) =>
      variant.name === selectedVariantName
        ? { ...variant, template: nextTemplate }
        : variant,
    ),
  };
}

function applyLayoutListTemplateById(
  props: LayoutListComponentProps,
  templateId: string,
  updater: (template: ContainerNode) => ContainerNode,
): LayoutListComponentProps {
  if (props.rowTemplate.id === templateId) {
    return {
      ...props,
      rowTemplate: updater(props.rowTemplate),
    };
  }
  if (!props.rowVariants || props.rowVariants.length === 0) {
    return props;
  }
  let hasChanges = false;
  const nextRowVariants = props.rowVariants.map((variant) => {
    if (variant.template.id !== templateId) return variant;
    hasChanges = true;
    return {
      ...variant,
      template: updater(variant.template),
    };
  });
  if (!hasChanges) return props;
  return {
    ...props,
    rowVariants: nextRowVariants,
  };
}

function getAllLayoutListRowTemplates(node: LayoutNode): ContainerNode[] {
  if (node.kind !== "component" || node.component !== "layoutList") return [];
  const props = (node.props ?? {}) as Partial<LayoutListComponentProps>;
  const variants = props.rowVariants ?? [];
  const templates: ContainerNode[] = [];
  if (props.rowTemplate) {
    templates.push(props.rowTemplate);
  }
  for (const variant of variants) {
    templates.push(variant.template);
  }
  return templates;
}

type LayoutNodeRuntimeSnapshot = {
  parentId: string | null;
  childIds: string[];
  signature: string;
};

function getLayoutNodeChildIds(node: LayoutNode): string[] {
  if (node.kind === "container") {
    return node.children.map((child) => child.id);
  }

  if (node.kind === "component" && node.component === "layoutList") {
    return getAllLayoutListRowTemplates(node).map((template) => template.id);
  }

  return [];
}

function getLayoutNodeSignature(node: LayoutNode): string {
  if (node.kind === "container") {
    const { children: _children, ...rest } = node;
    return JSON.stringify(rest);
  }

  if (node.kind === "component" && node.component === "layoutList") {
    const props = (node.props ?? {}) as Partial<LayoutListComponentProps>;
    const {
      rowTemplate: _rowTemplate,
      rowVariants: _rowVariants,
      ...restProps
    } = props;

    return JSON.stringify({
      ...node,
      props: {
        ...restProps,
        rowTemplateId: props.rowTemplate?.id,
        rowVariantTemplateIds: (props.rowVariants ?? []).map((variant) => ({
          name: variant.name,
          templateId: variant.template.id,
        })),
      },
    });
  }

  return JSON.stringify(node);
}

function collectLayoutNodeRuntimeSnapshots(
  nodes: LayoutNode[],
  parentId: string | null = null,
  snapshots = new Map<string, LayoutNodeRuntimeSnapshot>(),
): Map<string, LayoutNodeRuntimeSnapshot> {
  for (const node of nodes) {
    snapshots.set(node.id, {
      parentId,
      childIds: getLayoutNodeChildIds(node),
      signature: getLayoutNodeSignature(node),
    });

    for (const template of getAllLayoutListRowTemplates(node)) {
      collectLayoutNodeRuntimeSnapshots([template], node.id, snapshots);
    }

    if (node.kind === "container") {
      collectLayoutNodeRuntimeSnapshots(node.children, node.id, snapshots);
    }
  }

  return snapshots;
}

function arraysEqual(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

function diffRuntimeInvalidationNodeIds(
  previous: LayoutDocument,
  next: LayoutDocument,
): string[] {
  const previousNodes = collectLayoutNodeRuntimeSnapshots(
    previous.scene.children,
  );
  const nextNodes = collectLayoutNodeRuntimeSnapshots(next.scene.children);
  const touchedNodeIds = new Set<string>();
  const allNodeIds = new Set([...previousNodes.keys(), ...nextNodes.keys()]);

  for (const nodeId of allNodeIds) {
    const previousNode = previousNodes.get(nodeId);
    const nextNode = nextNodes.get(nodeId);

    if (!previousNode) {
      touchedNodeIds.add(nodeId);
      if (nextNode?.parentId) touchedNodeIds.add(nextNode.parentId);
      continue;
    }

    if (!nextNode) {
      touchedNodeIds.add(nodeId);
      if (previousNode.parentId) touchedNodeIds.add(previousNode.parentId);
      continue;
    }

    if (previousNode.parentId !== nextNode.parentId) {
      touchedNodeIds.add(nodeId);
      if (previousNode.parentId) touchedNodeIds.add(previousNode.parentId);
      if (nextNode.parentId) touchedNodeIds.add(nextNode.parentId);
    }

    if (!arraysEqual(previousNode.childIds, nextNode.childIds)) {
      touchedNodeIds.add(nodeId);
    }

    if (previousNode.signature !== nextNode.signature) {
      touchedNodeIds.add(nodeId);
    }
  }

  return [...touchedNodeIds];
}

function getLayoutListRowTemplate(node: LayoutNode): ContainerNode | undefined {
  if (node.kind !== "component" || node.component !== "layoutList") return;
  const props = (node.props ?? {}) as Partial<LayoutListComponentProps>;
  return resolveEditableLayoutListTemplate(props);
}

function updateNodeInContainerTree(
  container: ContainerNode,
  id: string,
  patch: Partial<LayoutNode>,
): ContainerNode {
  if (container.id === id) {
    return { ...container, ...patch } as ContainerNode;
  }
  return {
    ...container,
    children: updateNodeInTree(container.children, id, patch),
  };
}

function appendChildToContainerInTree(
  nodes: LayoutNode[],
  containerId: string,
  child: LayoutNode,
): LayoutNode[] {
  return nodes.map((node) => {
    if (node.kind === "container") {
      if (node.id === containerId) {
        const normalizedChild = normalizeNodeForContainerParent(
          child,
          node.layout ?? "absolute",
        );
        return {
          ...node,
          children: [...node.children, normalizedChild],
        };
      }
      if (node.children.length > 0) {
        return {
          ...node,
          children: appendChildToContainerInTree(
            node.children,
            containerId,
            child,
          ),
        };
      }
      return node;
    }

    const layoutListRowTemplate = getLayoutListRowTemplate(node);
    if (!layoutListRowTemplate) return node;

    if (layoutListRowTemplate.id === containerId) {
      const normalizedChild = normalizeNodeForContainerParent(
        child,
        layoutListRowTemplate.layout ?? "absolute",
      );
      const componentNode = node as ComponentNode;
      const props = (componentNode.props ?? {}) as LayoutListComponentProps;
      return {
        ...componentNode,
        props: applyEditableLayoutListTemplate(props, {
          ...layoutListRowTemplate,
          children: [...layoutListRowTemplate.children, normalizedChild],
        }),
      };
    }

    const nextRowTemplate = appendChildToContainerInTree(
      [layoutListRowTemplate],
      containerId,
      child,
    )[0] as ContainerNode;
    if (nextRowTemplate === layoutListRowTemplate) return node;
    const componentNode = node as ComponentNode;
    const props = (componentNode.props ?? {}) as LayoutListComponentProps;
    return {
      ...componentNode,
      props: applyEditableLayoutListTemplate(props, nextRowTemplate),
    };
  });
}
// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface EditorState {
  document: LayoutDocument;
  selectedNodeId: string | null;
  selectedNodeIds: string[];
  previewData: InjectedData;
  /** Overrides applied on top of getMockLayoutData() for preview. */
  mockOverrides: Partial<InjectedData>;
  /** Incrementing counters used to force-preview animations per node. */
  animationPreviewTicks: Record<string, number>;
  runtimeInvalidation: EditorRuntimeInvalidation;
  canUndo: boolean;
  canRedo: boolean;
}

export type EditorRuntimeInvalidation = {
  revision: number;
  nodeIds: readonly string[];
  /** Changes only when the editor preview renderer should be hard-remounted. */
  resetRevision: number;
};

interface SelectNodeOptions {
  additive?: boolean;
}

export interface LayoutPasteAnchor {
  x: number;
  y: number;
}

export interface EditorActions {
  setDocument: (doc: LayoutDocument) => void;
  /** Replays the editor preview from a fresh runtime without changing the document. */
  resetPreviewRuntime: () => void;
  refreshDocumentReference: () => void;
  selectNode: (id: string | null, options?: SelectNodeOptions) => void;
  addNode: (node: LayoutNode) => void;
  updateNode: (
    id: string,
    patch: Partial<LayoutNode>,
    options?: { history?: UpdateNodeHistoryOption },
  ) => void;
  /** Same as `updateNode` with merged undo history (typing / scrubbing). */
  updateNodeDebounced: (id: string, patch: Partial<LayoutNode>) => void;
  undo: () => boolean;
  redo: () => boolean;
  beginUndoGesture: () => void;
  endUndoGesture: () => void;
  prepareDebouncedUndo: () => void;
  scheduleDebouncedUndoCommit: (delayMs?: number) => void;
  deleteNode: (id: string) => void;
  deleteNodes: (ids: string[]) => void;
  moveNode: (id: string, x: number, y: number) => void;
  moveNodesByDelta: (ids: string[], dx: number, dy: number) => void;
  resizeNode: (id: string, width: number, height: number) => void;
  rotateNode: (id: string, rotation: number) => void;
  bringForward: (id: string) => void;
  sendBackward: (id: string) => void;
  bringForwardOne: (id: string) => void;
  sendBackwardOne: (id: string) => void;
  duplicateNode: (id: string) => void;
  copySelectedNodesToPayload: () => LayoutClipboardPayload | null;
  cutSelectedNodesToPayload: () => LayoutClipboardPayload | null;
  pasteLayoutPayload: (
    payload: LayoutClipboardPayload,
    anchor?: LayoutPasteAnchor,
    options?: { catalogToMerge?: SceneBehaviorCatalog },
  ) => string[];
  setMockOverrides: (overrides: Partial<InjectedData>) => void;
  updateMockField: (path: string, value: unknown) => void;
  triggerAnimationPreview: (nodeId: string) => void;
  groupSelectedNodes: (ids: string[]) => void;
  ungroupNode: (id: string) => void;
  extractNodeFromContainer: (id: string) => void;
  reorderNodeInTree: (activeId: string, overId: string) => void;
}

type EditorContextValue = EditorState & EditorActions;

// ---------------------------------------------------------------------------
// Default document
// ---------------------------------------------------------------------------

export function createEmptyDocument(
  type: "widget" | "display" = "display",
  canvasWidth = 1920,
  canvasHeight = 1080,
): LayoutDocument {
  return {
    version: 1,
    meta: {
      id: crypto.randomUUID(),
      name: "",
      type,
      canvas: { width: canvasWidth, height: canvasHeight },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    scene: {
      children: [],
      backgroundColor: "#ffffff",
      nodeNameCounters: {},
    },
  };
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

/** Recursively finds a node by id in the tree; returns null if not found. */
export function findNodeInTree(
  nodes: LayoutNode[],
  id: string,
): LayoutNode | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    const layoutListRowTemplates = getAllLayoutListRowTemplates(n);
    for (const layoutListRowTemplate of layoutListRowTemplates) {
      if (layoutListRowTemplate.id === id) return layoutListRowTemplate;
      const foundInRowTemplate = findNodeInTree(
        layoutListRowTemplate.children,
        id,
      );
      if (foundInRowTemplate) return foundInRowTemplate;
    }
    if (n.kind === "container") {
      const container = n as ContainerNode;
      if (container.children?.length) {
        const found = findNodeInTree(container.children, id);
        if (found) return found;
      }
    }
  }
  return null;
}

/** Recursively updates a node by id in the tree; returns new children array. */
function updateNodeInTree(
  nodes: LayoutNode[],
  id: string,
  patch: Partial<LayoutNode>,
): LayoutNode[] {
  return nodes.map((n) => {
    if (n.id === id) return { ...n, ...patch } as LayoutNode;
    const layoutListRowTemplates = getAllLayoutListRowTemplates(n);
    if (layoutListRowTemplates.length > 0) {
      for (const layoutListRowTemplate of layoutListRowTemplates) {
        if (layoutListRowTemplate.id === id) {
          const componentNode = n as ComponentNode;
          const props = (componentNode.props ?? {}) as LayoutListComponentProps;
          return {
            ...componentNode,
            props: applyLayoutListTemplateById(
              props,
              layoutListRowTemplate.id,
              (template) =>
                ({
                  ...template,
                  ...patch,
                }) as ContainerNode,
            ),
          } as LayoutNode;
        }
        const hasTargetInTemplate =
          findNodeInTree(layoutListRowTemplate.children, id) != null;
        if (!hasTargetInTemplate) {
          continue;
        }
        const nextRowTemplate = updateNodeInContainerTree(
          layoutListRowTemplate,
          id,
          patch,
        );
        if (nextRowTemplate !== layoutListRowTemplate) {
          const componentNode = n as ComponentNode;
          const props = (componentNode.props ?? {}) as LayoutListComponentProps;
          return {
            ...componentNode,
            props: applyLayoutListTemplateById(
              props,
              layoutListRowTemplate.id,
              () => nextRowTemplate,
            ),
          } as LayoutNode;
        }
      }
    }
    if (n.kind === "container") {
      const container = n as ContainerNode;
      if (container.children?.length) {
        return {
          ...n,
          children: updateNodeInTree(container.children, id, patch),
        } as LayoutNode;
      }
    }
    return n;
  });
}

/** Recursively removes a node by id from the tree. */
function removeNodeInTree(nodes: LayoutNode[], id: string): LayoutNode[] {
  return nodes
    .filter((n) => n.id !== id || n.locked === true)
    .map((n) => {
      const layoutListRowTemplates = getAllLayoutListRowTemplates(n);
      if (layoutListRowTemplates.length > 0) {
        let nextNode = n as ComponentNode;
        let hasChanges = false;
        for (const layoutListRowTemplate of layoutListRowTemplates) {
          if (layoutListRowTemplate.id === id) {
            // Root row templates cannot be removed from a layout list.
            continue;
          }
          const hasTargetInChildren =
            findNodeInTree(layoutListRowTemplate.children, id) != null;
          if (!hasTargetInChildren) {
            continue;
          }
          const nextChildren = removeNodeInTree(
            layoutListRowTemplate.children,
            id,
          );
          const props = (nextNode.props ?? {}) as LayoutListComponentProps;
          nextNode = {
            ...nextNode,
            props: applyLayoutListTemplateById(
              props,
              layoutListRowTemplate.id,
              (template) => ({
                ...template,
                children: nextChildren,
              }),
            ),
          };
          hasChanges = true;
        }
        if (hasChanges) {
          return nextNode as LayoutNode;
        }
      }
      if (n.kind === "container") {
        const container = n as ContainerNode;
        if (container.children?.length) {
          return {
            ...n,
            children: removeNodeInTree(container.children, id),
          } as LayoutNode;
        }
      }
      return n;
    });
}

interface NodeLocation {
  parentId: string | null;
  index: number;
  node: LayoutNode;
}

function findNodeLocation(
  nodes: LayoutNode[],
  id: string,
  parentId: string | null = null,
): NodeLocation | null {
  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];
    if (!node) continue;
    if (node.id === id) {
      return { parentId, index, node };
    }
    const layoutListRowTemplates = getAllLayoutListRowTemplates(node);
    for (const layoutListRowTemplate of layoutListRowTemplates) {
      if (layoutListRowTemplate.id === id) {
        return {
          parentId: node.id,
          index: 0,
          node: layoutListRowTemplate,
        };
      }
      if (layoutListRowTemplate.children.length > 0) {
        const foundInTemplate = findNodeLocation(
          layoutListRowTemplate.children,
          id,
          layoutListRowTemplate.id,
        );
        if (foundInTemplate) return foundInTemplate;
      }
    }
    if (node.kind === "container" && node.children.length > 0) {
      const found = findNodeLocation(node.children, id, node.id);
      if (found) return found;
    }
  }
  return null;
}

function replaceChildrenForParent(
  nodes: LayoutNode[],
  parentId: string | null,
  nextChildren: LayoutNode[],
): LayoutNode[] {
  if (parentId == null) return nextChildren;
  return nodes.map((node) => {
    const layoutListRowTemplates = getAllLayoutListRowTemplates(node);
    if (layoutListRowTemplates.length > 0) {
      for (const layoutListRowTemplate of layoutListRowTemplates) {
        if (layoutListRowTemplate.id !== parentId) continue;
        const componentNode = node as ComponentNode;
        const props = (componentNode.props ?? {}) as LayoutListComponentProps;
        return {
          ...componentNode,
          props: applyLayoutListTemplateById(
            props,
            layoutListRowTemplate.id,
            (template) => ({
              ...template,
              children: nextChildren,
            }),
          ),
        } as LayoutNode;
      }
      for (const layoutListRowTemplate of layoutListRowTemplates) {
        const hasParentInTemplate =
          findNodeInTree(layoutListRowTemplate.children, parentId) != null;
        if (!hasParentInTemplate) continue;
        const nextTemplateChildren = replaceChildrenForParent(
          layoutListRowTemplate.children,
          parentId,
          nextChildren,
        );
        const componentNode = node as ComponentNode;
        const props = (componentNode.props ?? {}) as LayoutListComponentProps;
        return {
          ...componentNode,
          props: applyLayoutListTemplateById(
            props,
            layoutListRowTemplate.id,
            (template) => ({
              ...template,
              children: nextTemplateChildren,
            }),
          ),
        } as LayoutNode;
      }
    }
    if (node.kind !== "container") return node;
    if (node.id === parentId) {
      return { ...node, children: nextChildren };
    }
    if (node.children.length === 0) return node;
    return {
      ...node,
      children: replaceChildrenForParent(node.children, parentId, nextChildren),
    };
  });
}

function isDescendantNode(node: LayoutNode, targetId: string): boolean {
  if (node.id === targetId) return true;
  if (node.kind !== "container") return false;
  return node.children.some((child) => isDescendantNode(child, targetId));
}

function getDuplicateOffset(): { dx: number; dy: number } {
  return { dx: 20, dy: 20 };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function updateSiblingsForNode(
  nodes: LayoutNode[],
  id: string,
  updater: (siblings: LayoutNode[]) => LayoutNode[],
): {
  nextNodes: LayoutNode[];
  location: NodeLocation | null;
  siblingsBefore: LayoutNode[];
  siblingsAfter: LayoutNode[];
} {
  const location = findNodeLocation(nodes, id);
  if (!location) {
    return {
      nextNodes: nodes,
      location: null,
      siblingsBefore: [],
      siblingsAfter: [],
    };
  }
  const siblingsBefore =
    location.parentId == null
      ? nodes
      : ((findNodeInTree(nodes, location.parentId) as ContainerNode | null)
          ?.children ?? []);
  const siblingsAfter = updater(siblingsBefore);
  if (siblingsAfter === siblingsBefore) {
    return {
      nextNodes: nodes,
      location,
      siblingsBefore,
      siblingsAfter,
    };
  }
  return {
    nextNodes: replaceChildrenForParent(
      nodes,
      location.parentId,
      siblingsAfter,
    ),
    location,
    siblingsBefore,
    siblingsAfter,
  };
}

function getDuplicatePosition(
  sourceNode: LayoutNode,
  canvasWidth: number,
  canvasHeight: number,
): { x: number; y: number; strategy: string } {
  const maxX = Math.max(0, canvasWidth - sourceNode.width);
  const maxY = Math.max(0, canvasHeight - sourceNode.height);
  const { dx, dy } = getDuplicateOffset();
  const candidates = [
    {
      x: clamp(sourceNode.x + dx, 0, maxX),
      y: clamp(sourceNode.y + dy, 0, maxY),
      strategy: "down-right",
    },
    {
      x: clamp(sourceNode.x + dx, 0, maxX),
      y: clamp(sourceNode.y - dy, 0, maxY),
      strategy: "up-right",
    },
    {
      x: clamp(sourceNode.x - dx, 0, maxX),
      y: clamp(sourceNode.y + dy, 0, maxY),
      strategy: "down-left",
    },
    {
      x: clamp(sourceNode.x - dx, 0, maxX),
      y: clamp(sourceNode.y - dy, 0, maxY),
      strategy: "up-left",
    },
  ];
  const best = candidates.reduce((acc, current) => {
    const currentDistance =
      Math.abs(current.x - sourceNode.x) + Math.abs(current.y - sourceNode.y);
    const bestDistance =
      Math.abs(acc.x - sourceNode.x) + Math.abs(acc.y - sourceNode.y);
    return currentDistance > bestDistance ? current : acc;
  });
  return best;
}

function normalizeSiblingZIndexes(nodes: LayoutNode[]): LayoutNode[] {
  const top = nodes.length;
  // Preserve visual stacking intent by normalizing from current z-index order,
  // not from raw array order (which would erase "bring forward/backward" actions).
  const sortedByZ = [...nodes].sort((a, b) => b.zIndex - a.zIndex);
  const normalizedById = new Map(
    sortedByZ.map((node, index) => [node.id, top - index] as const),
  );

  return nodes.map((node) => {
    const normalizedZ = normalizedById.get(node.id) ?? node.zIndex;
    const base = { ...node, zIndex: normalizedZ };
    if (base.kind !== "container") return base;
    return {
      ...base,
      children: normalizeSiblingZIndexes(base.children),
    };
  });
}

function hasSelectedAncestor(
  tree: LayoutNode[],
  nodeId: string,
  selectedIds: Set<string>,
): boolean {
  let currentLocation = findNodeLocation(tree, nodeId);
  while (currentLocation?.parentId != null) {
    if (selectedIds.has(currentLocation.parentId)) return true;
    currentLocation = findNodeLocation(tree, currentLocation.parentId);
  }
  return false;
}

export function getNodeAbsolutePosition(
  tree: LayoutNode[],
  nodeId: string,
): { x: number; y: number } | null {
  const location = findNodeLocation(tree, nodeId);
  if (!location) return null;
  let absoluteX = location.node.x;
  let absoluteY = location.node.y;
  let currentParentId = location.parentId;
  while (currentParentId != null) {
    const parentLocation = findNodeLocation(tree, currentParentId);
    if (!parentLocation) break;
    absoluteX += parentLocation.node.x;
    absoluteY += parentLocation.node.y;
    currentParentId = parentLocation.parentId;
  }
  return { x: absoluteX, y: absoluteY };
}

const EditorCtx = createContext<EditorContextValue | null>(null);

export function useEditor(): EditorContextValue {
  const ctx = useContext(EditorCtx);
  if (!ctx) throw new Error("useEditor must be used inside <EditorProvider>");
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface EditorProviderProps {
  initialDocument?: LayoutDocument;
  initialMockData?: InjectedData;
  onSave?: (doc: LayoutDocument) => void;
  children: React.ReactNode;
}

export const EditorProvider: React.FC<EditorProviderProps> = ({
  initialDocument,
  initialMockData,
  children,
}) => {
  const [document, setDocumentRaw] = useState<LayoutDocument>(() =>
    ensureLayoutNodeNames(
      cloneLayoutDocument(initialDocument ?? createEmptyDocument()),
    ),
  );
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  const [baseMockData] = useState<InjectedData>(
    () => initialMockData ?? getMockLayoutData(),
  );
  const [mockOverrides, setMockOverridesState] = useState<
    Partial<InjectedData>
  >({});
  const [animationPreviewTicks, setAnimationPreviewTicks] = useState<
    Record<string, number>
  >({});
  const [runtimeInvalidation, setRuntimeInvalidation] =
    useState<EditorRuntimeInvalidation>({
      revision: 0,
      nodeIds: [],
      resetRevision: 0,
    });
  const previewData = useMemo(
    () => ({ ...baseMockData, ...mockOverrides }),
    [baseMockData, mockOverrides],
  );

  const documentRef = useRef(document);
  const selectedNodeIdRef = useRef(selectedNodeId);
  const selectedNodeIdsRef = useRef(selectedNodeIds);
  useEffect(() => {
    documentRef.current = document;
  }, [document]);
  useEffect(() => {
    selectedNodeIdRef.current = selectedNodeId;
  }, [selectedNodeId]);
  useEffect(() => {
    selectedNodeIdsRef.current = selectedNodeIds;
  }, [selectedNodeIds]);

  const pastStackRef = useRef<LayoutDocument[]>([]);
  const futureStackRef = useRef<LayoutDocument[]>([]);
  const isApplyingHistoryRef = useRef(false);
  const gestureDepthRef = useRef(0);
  const gestureBaselineRef = useRef<LayoutDocument | null>(null);
  const debounceBaselineRef = useRef<LayoutDocument | null>(null);
  /** Browser timers use numeric IDs (DOM lib); avoids NodeJS.Timeout mismatch in CI. */
  const debounceTimerRef = useRef<number | null>(null);
  const lastSyncedInitialIdRef = useRef<string | null>(null);
  const markRuntimeInvalidation = useCallback((nodeIds: readonly string[]) => {
    const uniqueNodeIds = [...new Set(nodeIds.filter(Boolean))];
    setRuntimeInvalidation((current) => ({
      revision: current.revision + 1,
      nodeIds: uniqueNodeIds,
      resetRevision: current.resetRevision,
    }));
  }, []);
  const applyDocumentWithRuntimeInvalidation = useCallback(
    (nextDocument: LayoutDocument, previousDocument = documentRef.current) => {
      documentRef.current = nextDocument;
      setDocumentRaw(nextDocument);
      markRuntimeInvalidation(
        diffRuntimeInvalidationNodeIds(previousDocument, nextDocument),
      );
    },
    [markRuntimeInvalidation],
  );

  const [historyEpoch, setHistoryEpoch] = useState(0);
  const bumpHistory = useCallback(() => {
    setHistoryEpoch((value) => value + 1);
  }, []);

  const commitBeforeMutation = useCallback(() => {
    if (isApplyingHistoryRef.current) return;
    if (gestureDepthRef.current > 0) return;
    if (debounceBaselineRef.current !== null) return;

    const snapshot = cloneLayoutDocument(documentRef.current);
    pastStackRef.current = [...pastStackRef.current, snapshot].slice(
      -MAX_UNDO_STACK,
    );
    futureStackRef.current = [];
    bumpHistory();
  }, [bumpHistory]);

  const prepareDebouncedUndo = useCallback(() => {
    if (debounceBaselineRef.current === null) {
      debounceBaselineRef.current = cloneLayoutDocument(documentRef.current);
    }
  }, []);

  const flushDebouncedUndoBaseline = useCallback(() => {
    const baseline = debounceBaselineRef.current;
    debounceBaselineRef.current = null;
    if (!baseline || isApplyingHistoryRef.current) return;
    if (layoutDocumentsEqual(baseline, documentRef.current)) return;
    pastStackRef.current = [...pastStackRef.current, baseline].slice(
      -MAX_UNDO_STACK,
    );
    futureStackRef.current = [];
    bumpHistory();
  }, [bumpHistory]);

  const scheduleDebouncedUndoCommit = useCallback(
    (delayMs: number = DEBOUNCE_HISTORY_MS) => {
      if (debounceTimerRef.current != null) {
        window.clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = window.setTimeout(() => {
        debounceTimerRef.current = null;
        flushDebouncedUndoBaseline();
      }, delayMs);
    },
    [flushDebouncedUndoBaseline],
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current != null) {
        window.clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const beginUndoGesture = useCallback(() => {
    gestureDepthRef.current += 1;
    if (gestureDepthRef.current === 1) {
      gestureBaselineRef.current = cloneLayoutDocument(documentRef.current);
    }
  }, []);

  const endUndoGesture = useCallback(() => {
    if (gestureDepthRef.current <= 0) return;
    gestureDepthRef.current -= 1;
    if (gestureDepthRef.current > 0) return;
    const baseline = gestureBaselineRef.current;
    gestureBaselineRef.current = null;
    if (!baseline || isApplyingHistoryRef.current) return;
    if (layoutDocumentsEqual(baseline, documentRef.current)) return;
    pastStackRef.current = [...pastStackRef.current, baseline].slice(
      -MAX_UNDO_STACK,
    );
    futureStackRef.current = [];
    bumpHistory();
  }, [bumpHistory]);

  const pruneSelectionForDocument = useCallback((doc: LayoutDocument) => {
    const roots = doc.scene.children;
    const prevIds = selectedNodeIdsRef.current;
    const prevPrimary = selectedNodeIdRef.current;
    const filteredIds = prevIds.filter(
      (id) => findNodeInTree(roots, id) != null,
    );
    const primaryStillValid =
      prevPrimary != null && findNodeInTree(roots, prevPrimary) != null;
    const nextPrimary = primaryStillValid
      ? prevPrimary
      : (filteredIds[0] ?? null);
    const nextIds =
      filteredIds.length > 0
        ? filteredIds
        : nextPrimary != null
          ? [nextPrimary]
          : [];
    setSelectedNodeIds(nextIds);
    setSelectedNodeId(nextPrimary);
  }, []);

  const undo = useCallback((): boolean => {
    if (pastStackRef.current.length === 0) return false;
    debounceBaselineRef.current = null;
    if (debounceTimerRef.current != null) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    const snapshot = pastStackRef.current[pastStackRef.current.length - 1]!;
    pastStackRef.current = pastStackRef.current.slice(0, -1);
    futureStackRef.current = [
      cloneLayoutDocument(documentRef.current),
      ...futureStackRef.current,
    ];
    isApplyingHistoryRef.current = true;
    applyDocumentWithRuntimeInvalidation(snapshot);
    isApplyingHistoryRef.current = false;
    pruneSelectionForDocument(snapshot);
    bumpHistory();
    return true;
  }, [
    applyDocumentWithRuntimeInvalidation,
    bumpHistory,
    pruneSelectionForDocument,
  ]);

  const redo = useCallback((): boolean => {
    if (futureStackRef.current.length === 0) return false;
    debounceBaselineRef.current = null;
    if (debounceTimerRef.current != null) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    const snapshot = futureStackRef.current[0]!;
    futureStackRef.current = futureStackRef.current.slice(1);
    pastStackRef.current = [
      ...pastStackRef.current,
      cloneLayoutDocument(documentRef.current),
    ].slice(-MAX_UNDO_STACK);
    isApplyingHistoryRef.current = true;
    applyDocumentWithRuntimeInvalidation(snapshot);
    isApplyingHistoryRef.current = false;
    pruneSelectionForDocument(snapshot);
    bumpHistory();
    return true;
  }, [
    applyDocumentWithRuntimeInvalidation,
    bumpHistory,
    pruneSelectionForDocument,
  ]);

  useEffect(() => {
    if (!initialDocument) return;
    const id = initialDocument.meta.id;

    if (lastSyncedInitialIdRef.current === id) {
      const current = documentRef.current;
      const nextCanvas = initialDocument.meta.canvas;
      if (
        current.meta.canvas.width !== nextCanvas.width ||
        current.meta.canvas.height !== nextCanvas.height ||
        current.scene.backgroundColor !== initialDocument.scene.backgroundColor
      ) {
        isApplyingHistoryRef.current = true;
        applyDocumentWithRuntimeInvalidation({
          ...current,
          meta: {
            ...current.meta,
            canvas: { ...nextCanvas },
          },
          scene: {
            ...current.scene,
            backgroundColor: initialDocument.scene.backgroundColor,
          },
        });
        isApplyingHistoryRef.current = false;
      }
      return;
    }

    lastSyncedInitialIdRef.current = id;
    pastStackRef.current = [];
    futureStackRef.current = [];
    gestureDepthRef.current = 0;
    gestureBaselineRef.current = null;
    debounceBaselineRef.current = null;
    if (debounceTimerRef.current != null) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    isApplyingHistoryRef.current = true;
    setDocumentRaw(
      ensureLayoutNodeNames({
        ...cloneLayoutDocument(initialDocument),
        meta: {
          ...initialDocument.meta,
          updatedAt: new Date().toISOString(),
        },
      }),
    );
    isApplyingHistoryRef.current = false;
    setSelectedNodeId(null);
    setSelectedNodeIds([]);
    bumpHistory();
  }, [initialDocument, applyDocumentWithRuntimeInvalidation, bumpHistory]);

  const setMockOverrides = useCallback((overrides: Partial<InjectedData>) => {
    setMockOverridesState((prev) => ({ ...prev, ...overrides }));
  }, []);

  const updateMockField = useCallback(
    (path: string, value: unknown) => {
      const [head, ...rest] = path.split(".");
      if (!head) return;
      setMockOverridesState((prev) => {
        if (rest.length === 0) return { ...prev, [head]: value };
        const baseValue = baseMockData[head as keyof InjectedData];
        const previousValue = prev[head as keyof InjectedData];
        const mergedRoot = deepMergeMockValue(baseValue, previousValue);
        const nextValue = setMockValueAtPath(mergedRoot, rest, value);
        return { ...prev, [head]: nextValue };
      });
    },
    [baseMockData],
  );

  const setDocument = useCallback(
    (doc: LayoutDocument) => {
      commitBeforeMutation();
      applyDocumentWithRuntimeInvalidation(
        ensureLayoutNodeNames({
          ...doc,
          meta: { ...doc.meta, updatedAt: new Date().toISOString() },
        }),
      );
    },
    [applyDocumentWithRuntimeInvalidation, commitBeforeMutation],
  );

  const resetPreviewRuntime = useCallback(() => {
    setRuntimeInvalidation((current) => ({
      revision: current.revision + 1,
      nodeIds: [],
      resetRevision: current.resetRevision + 1,
    }));
  }, []);

  const refreshDocumentReference = resetPreviewRuntime;

  const triggerAnimationPreview = useCallback((nodeId: string) => {
    setAnimationPreviewTicks((prev) => {
      const nextTick = (prev[nodeId] ?? 0) + 1;
      return {
        ...prev,
        [nodeId]: nextTick,
      };
    });
  }, []);

  const updateScene = useCallback(
    (updater: (scene: LayoutDocument["scene"]) => LayoutDocument["scene"]) => {
      commitBeforeMutation();
      const previous = documentRef.current;
      const nextScene = updater(previous.scene);
      const nextDocument = {
        ...previous,
        meta: { ...previous.meta, updatedAt: new Date().toISOString() },
        scene: {
          ...nextScene,
          children: normalizeSiblingZIndexes(nextScene.children),
        },
      };
      applyDocumentWithRuntimeInvalidation(nextDocument, previous);
    },
    [applyDocumentWithRuntimeInvalidation, commitBeforeMutation],
  );

  const updateChildren = useCallback(
    (updater: (children: LayoutNode[]) => LayoutNode[]) => {
      updateScene((scene) => ({
        ...scene,
        children: updater(scene.children),
      }));
    },
    [updateScene],
  );

  const groupSelectedNodes = useCallback(
    (ids: string[]) => {
      if (ids.length < 1) return;
      let groupId: string | null = null;
      updateScene((scene) => {
        const children = scene.children;
        const uniqueIds = [...new Set(ids)];
        if (uniqueIds.length < 1) return scene;
        const locations = uniqueIds
          .map((id) => findNodeLocation(children, id))
          .filter((location): location is NodeLocation => location != null);
        if (locations.length < 1) return scene;
        const parentId = locations[0]?.parentId ?? null;
        const sameParent = locations.every(
          (location) => location.parentId === parentId,
        );
        if (!sameParent) return scene;

        const parentChildren =
          parentId == null
            ? children
            : ((findNodeInTree(children, parentId) as ContainerNode | null)
                ?.children ?? []);
        if (parentChildren.length === 0) return scene;

        const selectedByIndex = locations
          .map((location) => ({
            ...location,
            node: parentChildren[location.index],
          }))
          .filter(
            (entry): entry is NodeLocation & { node: LayoutNode } =>
              entry.node != null,
          )
          .sort((a, b) => a.index - b.index);
        if (selectedByIndex.length < 1) return scene;

        const minX = Math.min(...selectedByIndex.map((entry) => entry.node.x));
        const minY = Math.min(...selectedByIndex.map((entry) => entry.node.y));
        const maxX = Math.max(
          ...selectedByIndex.map((entry) => entry.node.x + entry.node.width),
        );
        const maxY = Math.max(
          ...selectedByIndex.map((entry) => entry.node.y + entry.node.height),
        );

        const maxSelectedZIndex = Math.max(
          ...selectedByIndex.map((entry) => entry.node.zIndex),
        );
        const childrenByZIndexDesc = [...selectedByIndex].sort(
          (a, b) => b.node.zIndex - a.node.zIndex,
        );
        const childZIndexById = new Map<string, number>();
        childrenByZIndexDesc.forEach((entry, offset) => {
          childZIndexById.set(entry.node.id, maxSelectedZIndex - offset);
        });
        const groupNodeBase: ContainerNode = {
          id: crypto.randomUUID(),
          kind: "container",
          layout: "flex",
          x: minX,
          y: minY,
          width: Math.max(1, maxX - minX),
          height: Math.max(1, maxY - minY),
          zIndex: maxSelectedZIndex,
          label: undefined,
          children: normalizePastedNodesForParentLayout(
            selectedByIndex.map((entry) => ({
              ...entry.node,
              x: entry.node.x - minX,
              y: entry.node.y - minY,
              zIndex: childZIndexById.get(entry.node.id) ?? entry.node.zIndex,
            })),
            "flex",
          ),
        };
        const allocated = allocateNameForNode(
          groupNodeBase,
          scene.nodeNameCounters ?? {},
        );
        const groupNode = allocated.node as ContainerNode;
        groupId = groupNode.id;

        const firstIndex = selectedByIndex[0]?.index ?? 0;
        const selectedIdsSet = new Set(
          selectedByIndex.map((entry) => entry.node.id),
        );
        const nextSiblings = parentChildren.filter(
          (node) => !selectedIdsSet.has(node.id),
        );
        nextSiblings.splice(firstIndex, 0, groupNode);

        return {
          ...scene,
          children: replaceChildrenForParent(children, parentId, nextSiblings),
          nodeNameCounters: allocated.counters,
        };
      });
      if (!groupId) return;
      setSelectedNodeId(groupId);
      setSelectedNodeIds([groupId]);
    },
    [updateScene],
  );

  const ungroupNode = useCallback(
    (id: string) => {
      updateChildren((children) => {
        const location = findNodeLocation(children, id);
        if (!location || location.node.kind !== "container") return children;
        const groupNode = location.node;
        const parentId = location.parentId;
        const parentChildren =
          parentId == null
            ? children
            : ((findNodeInTree(children, parentId) as ContainerNode | null)
                ?.children ?? []);
        if (parentChildren.length === 0) return children;

        const expandedChildren = groupNode.children.map((child, index) => ({
          ...child,
          x: groupNode.x + child.x,
          y: groupNode.y + child.y,
          zIndex: groupNode.zIndex + index,
        }));

        const nextSiblings = [...parentChildren];
        nextSiblings.splice(location.index, 1, ...expandedChildren);

        const nextSelectedIds = expandedChildren.map((child) => child.id);
        setSelectedNodeIds(nextSelectedIds);
        setSelectedNodeId(nextSelectedIds[0] ?? null);

        return replaceChildrenForParent(children, parentId, nextSiblings);
      });
    },
    [updateChildren],
  );

  const extractNodeFromContainer = useCallback(
    (id: string) => {
      updateChildren((children) => {
        const location = findNodeLocation(children, id);
        if (!location || location.parentId == null) return children;

        const parentLocation = findNodeLocation(children, location.parentId);
        if (!parentLocation || parentLocation.node.kind !== "container") {
          return children;
        }

        const parentContainer = parentLocation.node as ContainerNode;
        const parentChildren = [...parentContainer.children];
        const nodeToExtract = parentChildren[location.index];
        if (!nodeToExtract) return children;

        parentChildren.splice(location.index, 1);

        const shouldForceAbsolute =
          nodeToExtract.style?.position === "relative";
        const extractedNode = {
          ...nodeToExtract,
          x: parentContainer.x + nodeToExtract.x,
          y: parentContainer.y + nodeToExtract.y,
          ...(shouldForceAbsolute
            ? {
                style: {
                  ...asPlainObject(nodeToExtract.style),
                  position: "absolute",
                },
              }
            : {}),
        } as LayoutNode;

        const grandParentId = parentLocation.parentId;
        const parentLevelSiblings =
          grandParentId == null
            ? children
            : ((findNodeInTree(children, grandParentId) as ContainerNode | null)
                ?.children ?? []);

        if (parentLevelSiblings.length === 0 && grandParentId != null) {
          return children;
        }

        const nextParentLevelSiblings = [...parentLevelSiblings];
        nextParentLevelSiblings[parentLocation.index] = {
          ...parentContainer,
          children: parentChildren,
        };
        nextParentLevelSiblings.splice(
          parentLocation.index + 1,
          0,
          extractedNode,
        );

        setSelectedNodeIds([extractedNode.id]);
        setSelectedNodeId(extractedNode.id);

        return replaceChildrenForParent(
          children,
          grandParentId,
          nextParentLevelSiblings,
        );
      });
    },
    [updateChildren],
  );

  const reorderNodeInTree = useCallback(
    (activeId: string, overId: string) => {
      if (activeId === overId) return;
      updateChildren((children) => {
        const overLocation = findNodeLocation(children, overId);
        if (!overLocation) return children;
        const activeLocation = findNodeLocation(children, activeId);
        if (!activeLocation) return children;
        if (isDescendantNode(activeLocation.node, overId)) return children;
        if (activeLocation.parentId !== overLocation.parentId) return children;

        const siblings =
          activeLocation.parentId == null
            ? children
            : ((
                findNodeInTree(
                  children,
                  activeLocation.parentId,
                ) as ContainerNode | null
              )?.children ?? []);
        if (siblings.length <= 1) return children;

        const sortedByZ = [...siblings].sort((a, b) => b.zIndex - a.zIndex);
        const fromIndex = sortedByZ.findIndex((node) => node.id === activeId);
        const toIndex = sortedByZ.findIndex((node) => node.id === overId);
        if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex)
          return children;

        const moved = [...sortedByZ];
        const [activeNode] = moved.splice(fromIndex, 1);
        if (!activeNode) return children;
        moved.splice(toIndex, 0, activeNode);

        const top = moved.length;
        const zById = new Map(
          moved.map((node, index) => [node.id, top - index] as const),
        );
        const nextSiblings = siblings.map((node) => {
          const nextZ = zById.get(node.id);
          return nextZ == null
            ? node
            : ({ ...node, zIndex: nextZ } as LayoutNode);
        });

        return replaceChildrenForParent(
          children,
          activeLocation.parentId,
          nextSiblings,
        );
      });
    },
    [updateChildren],
  );

  const selectNode = useCallback(
    (id: string | null, options?: SelectNodeOptions) => {
      if (id == null) {
        setSelectedNodeId(null);
        setSelectedNodeIds([]);
        return;
      }

      if (options?.additive) {
        setSelectedNodeIds((prev) => {
          const exists = prev.includes(id);
          const next = exists
            ? prev.filter((nodeId) => nodeId !== id)
            : [...prev, id];
          setSelectedNodeId(
            next.length > 0 ? (next[next.length - 1] ?? null) : null,
          );
          return next;
        });
        return;
      }

      setSelectedNodeId(id);
      setSelectedNodeIds([id]);
    },
    [],
  );

  const addNode = useCallback(
    (node: LayoutNode) => {
      const nodeWithDefaults = addNodeDefaultValues(node);
      let createdId = nodeWithDefaults.id;
      updateScene((scene) => {
        const allocated = allocateNamesForTree(
          nodeWithDefaults,
          scene.nodeNameCounters ?? {},
        );
        createdId = allocated.node.id;
        const children = (() => {
          if (!selectedNodeId) return [...scene.children, allocated.node];
          const selectedNode = findNodeInTree(scene.children, selectedNodeId);
          if (selectedNode?.kind !== "container") {
            return [...scene.children, allocated.node];
          }
          return appendChildToContainerInTree(
            scene.children,
            selectedNodeId,
            allocated.node,
          );
        })();
        return {
          ...scene,
          children,
          nodeNameCounters: allocated.counters,
        };
      });
      setSelectedNodeId(createdId);
      setSelectedNodeIds([createdId]);
    },
    [selectedNodeId, updateScene],
  );

  const updateNode = useCallback(
    (
      id: string,
      patch: Partial<LayoutNode>,
      options?: { history?: UpdateNodeHistoryOption },
    ) => {
      if (options?.history === "debounced") {
        prepareDebouncedUndo();
      }
      updateChildren((ch) => updateNodeInTree(ch, id, patch));
      if (options?.history === "debounced") {
        scheduleDebouncedUndoCommit();
      }
    },
    [prepareDebouncedUndo, scheduleDebouncedUndoCommit, updateChildren],
  );

  const updateNodeDebounced = useCallback(
    (id: string, patch: Partial<LayoutNode>) => {
      updateNode(id, patch, { history: "debounced" });
    },
    [updateNode],
  );

  const deleteNode = useCallback(
    (id: string) => {
      const target = findNodeInTree(documentRef.current.scene.children, id);
      if (target?.locked === true) {
        return;
      }
      updateChildren((ch) => removeNodeInTree(ch, id));
      setSelectedNodeId((prev) => (prev === id ? null : prev));
      setSelectedNodeIds((prev) => prev.filter((nodeId) => nodeId !== id));
    },
    [updateChildren],
  );
  const deleteNodes = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return;
      const deletableIds = ids.filter((id) => {
        const target = findNodeInTree(documentRef.current.scene.children, id);
        return target?.locked !== true;
      });
      if (deletableIds.length === 0) return;
      const idsSet = new Set(deletableIds);
      updateChildren((children) =>
        deletableIds.reduce(
          (currentChildren, currentId) =>
            removeNodeInTree(currentChildren, currentId),
          children,
        ),
      );
      setSelectedNodeId((previous) =>
        previous && idsSet.has(previous) ? null : previous,
      );
      setSelectedNodeIds((previous) =>
        previous.filter((nodeId) => !idsSet.has(nodeId)),
      );
    },
    [updateChildren],
  );

  const moveNode = useCallback(
    (id: string, x: number, y: number) =>
      updateNode(id, { x, y } as Partial<LayoutNode>),
    [updateNode],
  );

  const moveNodesByDelta = useCallback(
    (ids: string[], dx: number, dy: number) => {
      if (ids.length === 0 || (dx === 0 && dy === 0)) return;
      const targetIds = [...new Set(ids)];
      updateChildren((children) =>
        targetIds.reduce((currentChildren, id) => {
          const node = findNodeInTree(currentChildren, id);
          if (!node) return currentChildren;
          return updateNodeInTree(currentChildren, id, {
            x: node.x + dx,
            y: node.y + dy,
          } as Partial<LayoutNode>);
        }, children),
      );
    },
    [updateChildren],
  );

  const resizeNode = useCallback(
    (id: string, width: number, height: number) => {
      updateChildren((nodes) => {
        const current = findNodeInTree(nodes, id);
        if (
          current?.kind === "component" &&
          (current as ComponentNode).component === "barLoader"
        ) {
          const n = current as ComponentNode;
          const barLoaderProps = (n.props ?? {}) as BarLoaderProps;
          const synced = syncBarLoaderDiskStylesToNodeBounds(
            barLoaderProps,
            barLoaderProps.diskStyles,
            width,
            height,
          );
          return updateNodeInTree(nodes, id, {
            width: Math.round(width),
            height: Math.round(height),
            props: {
              ...n.props,
              diskStyles: synced.diskStyles,
              ...(synced.diskDefaultStyles !== undefined
                ? { diskDefaultStyles: synced.diskDefaultStyles }
                : {}),
            } as ComponentNode["props"],
          } as Partial<LayoutNode>);
        }
        return updateNodeInTree(nodes, id, {
          width,
          height,
        } as Partial<LayoutNode>);
      });
    },
    [updateChildren],
  );

  const rotateNode = useCallback(
    (id: string, rotation: number) =>
      updateNode(id, { rotation } as Partial<LayoutNode>),
    [updateNode],
  );

  const bringForward = useCallback(
    (id: string) =>
      updateChildren((nodes) => {
        const result = updateSiblingsForNode(nodes, id, (siblings) => {
          const maxZ = Math.max(...siblings.map((n) => n.zIndex), 0);
          return siblings.map((n) =>
            n.id === id ? ({ ...n, zIndex: maxZ + 1 } as LayoutNode) : n,
          );
        });
        return result.nextNodes;
      }),
    [updateChildren],
  );

  const sendBackward = useCallback(
    (id: string) =>
      updateChildren((nodes) => {
        const result = updateSiblingsForNode(nodes, id, (siblings) => {
          const minZ = Math.min(...siblings.map((n) => n.zIndex), 0);
          return siblings.map((n) =>
            n.id === id ? ({ ...n, zIndex: minZ - 1 } as LayoutNode) : n,
          );
        });
        return result.nextNodes;
      }),
    [updateChildren],
  );

  const bringForwardOne = useCallback(
    (id: string) =>
      updateChildren((nodes) => {
        const result = updateSiblingsForNode(nodes, id, (siblings) => {
          const sorted = [...siblings].sort((a, b) => a.zIndex - b.zIndex);
          const index = sorted.findIndex((node) => node.id === id);
          if (index < 0 || index >= sorted.length - 1) {
            return siblings;
          }
          const current = sorted[index];
          const next = sorted[index + 1];
          if (!current || !next) {
            return siblings;
          }
          return siblings.map((node) => {
            if (node.id === current.id) {
              return { ...node, zIndex: next.zIndex } as LayoutNode;
            }
            if (node.id === next.id) {
              return { ...node, zIndex: current.zIndex } as LayoutNode;
            }
            return node;
          });
        });
        return result.nextNodes;
      }),
    [updateChildren],
  );

  const sendBackwardOne = useCallback(
    (id: string) =>
      updateChildren((nodes) => {
        const result = updateSiblingsForNode(nodes, id, (siblings) => {
          const sorted = [...siblings].sort((a, b) => a.zIndex - b.zIndex);
          const index = sorted.findIndex((node) => node.id === id);
          if (index <= 0) {
            return siblings;
          }
          const current = sorted[index];
          const previous = sorted[index - 1];
          if (!current || !previous) {
            return siblings;
          }
          return siblings.map((node) => {
            if (node.id === current.id) {
              return { ...node, zIndex: previous.zIndex } as LayoutNode;
            }
            if (node.id === previous.id) {
              return { ...node, zIndex: current.zIndex } as LayoutNode;
            }
            return node;
          });
        });
        return result.nextNodes;
      }),
    [updateChildren],
  );

  const duplicateNode = useCallback(
    (id: string) => {
      let duplicatedId: string | null = null;
      updateScene((scene) => {
        const children = scene.children;
        const location = findNodeLocation(children, id);
        if (!location) return scene;
        const parentChildren =
          location.parentId == null
            ? children
            : ((
                findNodeInTree(
                  children,
                  location.parentId,
                ) as ContainerNode | null
              )?.children ?? []);
        const sourceNode = parentChildren[location.index];
        if (!sourceNode) return scene;
        const duplicate = cloneNodeWithFreshIds(sourceNode);
        const position = getDuplicatePosition(
          sourceNode,
          document.meta.canvas.width,
          document.meta.canvas.height,
        );
        duplicate.x = position.x;
        duplicate.y = position.y;
        const allocated = allocateNamesForTree(
          duplicate,
          scene.nodeNameCounters ?? {},
        );
        duplicatedId = allocated.node.id;
        const nextSiblings = [...parentChildren];
        // Insert clone right before source so it is immediately above it after z-index normalization.
        nextSiblings.splice(location.index, 0, allocated.node);
        return {
          ...scene,
          children: replaceChildrenForParent(
            children,
            location.parentId,
            nextSiblings,
          ),
          nodeNameCounters: allocated.counters,
        };
      });
      if (!duplicatedId) return;
      setSelectedNodeId(duplicatedId);
      setSelectedNodeIds([duplicatedId]);
    },
    [document.meta.canvas.height, document.meta.canvas.width, updateScene],
  );

  const copySelectedNodesToPayload =
    useCallback((): LayoutClipboardPayload | null => {
      if (selectedNodeIds.length === 0) return null;
      const selectedSet = new Set(selectedNodeIds);
      const rootSelectionIds = selectedNodeIds.filter(
        (id) => !hasSelectedAncestor(document.scene.children, id, selectedSet),
      );
      const nodes = rootSelectionIds
        .map((id) => findNodeInTree(document.scene.children, id))
        .filter((node): node is LayoutNode => node != null)
        .map((node) => structuredClone(node));
      if (nodes.length === 0) return null;
      const catalog = collectReferencedSceneCatalog(
        nodes,
        document.scene.catalog,
      );
      return {
        kind: "meet-manager/editor-clipboard",
        version: 1,
        entity: "layout",
        source: {
          layoutId: document.meta.id,
          tabType: "display",
          copiedAt: new Date().toISOString(),
        },
        data: {
          nodes,
          ...(sceneCatalogHasEntries(catalog) ? { catalog } : {}),
        },
      };
    }, [
      document.meta.id,
      document.scene.catalog,
      document.scene.children,
      selectedNodeIds,
    ]);

  const cutSelectedNodesToPayload =
    useCallback((): LayoutClipboardPayload | null => {
      const payload = copySelectedNodesToPayload();
      if (!payload) return null;
      const selectedSet = new Set(selectedNodeIds);
      const rootSelectionIds = selectedNodeIds.filter(
        (id) => !hasSelectedAncestor(document.scene.children, id, selectedSet),
      );
      updateChildren((children) =>
        rootSelectionIds.reduce(
          (acc, id) => removeNodeInTree(acc, id),
          children,
        ),
      );
      setSelectedNodeId(null);
      setSelectedNodeIds([]);
      return payload;
    }, [
      copySelectedNodesToPayload,
      document.scene.children,
      selectedNodeIds,
      updateChildren,
    ]);

  const pasteLayoutPayload = useCallback(
    (
      payload: LayoutClipboardPayload,
      anchor?: LayoutPasteAnchor,
      options?: { catalogToMerge?: SceneBehaviorCatalog },
    ): string[] => {
      if (payload.data.nodes.length === 0) return [];
      const pastedIds: string[] = [];
      const sourceMinX = Math.min(...payload.data.nodes.map((node) => node.x));
      const sourceMinY = Math.min(...payload.data.nodes.map((node) => node.y));
      const previous = documentRef.current;
      let counters = previous.scene.nodeNameCounters ?? {};
      const clones = payload.data.nodes.map((sourceNode) => {
        const duplicate = cloneNodeWithFreshIds(sourceNode);
        if (anchor) {
          duplicate.x = sourceNode.x - sourceMinX + anchor.x;
          duplicate.y = sourceNode.y - sourceMinY + anchor.y;
        } else {
          duplicate.x += 40;
          duplicate.y += 40;
        }
        const allocated = allocateNamesForTree(duplicate, counters);
        counters = allocated.counters;
        pastedIds.push(allocated.node.id);
        return allocated.node;
      });

      const targetContainerId =
        selectedNodeId != null &&
        findNodeInTree(document.scene.children, selectedNodeId)?.kind ===
          "container"
          ? selectedNodeId
          : null;
      const targetContainer =
        targetContainerId != null
          ? findNodeInTree(document.scene.children, targetContainerId)
          : null;
      const pasteParentLayout =
        targetContainer?.kind === "container" &&
        targetContainer.layout === "flex"
          ? "flex"
          : "absolute";
      const normalizedClones = normalizePastedNodesForParentLayout(
        clones,
        pasteParentLayout,
      );
      const catalogToMerge = options?.catalogToMerge;

      commitBeforeMutation();
      const nextChildren = (() => {
        if (!targetContainerId) {
          return normalizeSiblingZIndexes([
            ...previous.scene.children,
            ...normalizedClones,
          ]);
        }
        const targetContainerAbsPosition = getNodeAbsolutePosition(
          previous.scene.children,
          targetContainerId,
        );
        const clonesForContainer =
          anchor && targetContainerAbsPosition
            ? normalizedClones.map((clone) => ({
                ...clone,
                x: clone.x - targetContainerAbsPosition.x,
                y: clone.y - targetContainerAbsPosition.y,
              }))
            : normalizedClones;
        return normalizeSiblingZIndexes(
          clonesForContainer.reduce(
            (acc, clone) =>
              appendChildToContainerInTree(acc, targetContainerId, clone),
            previous.scene.children,
          ),
        );
      })();
      const nextDocument = {
        ...previous,
        meta: { ...previous.meta, updatedAt: new Date().toISOString() },
        scene: {
          ...previous.scene,
          children: nextChildren,
          nodeNameCounters: counters,
          ...(catalogToMerge
            ? {
                catalog: mergeSceneCatalogs(
                  previous.scene.catalog,
                  catalogToMerge,
                ),
              }
            : {}),
        },
      };
      applyDocumentWithRuntimeInvalidation(nextDocument, previous);
      setSelectedNodeId(pastedIds[0] ?? null);
      setSelectedNodeIds(pastedIds);
      return pastedIds;
    },
    [
      applyDocumentWithRuntimeInvalidation,
      commitBeforeMutation,
      document.scene.children,
      selectedNodeId,
    ],
  );

  const value: EditorContextValue = useMemo(
    () => ({
      document,
      selectedNodeId,
      selectedNodeIds,
      previewData,
      mockOverrides,
      animationPreviewTicks,
      runtimeInvalidation,
      canUndo: pastStackRef.current.length > 0,
      canRedo: futureStackRef.current.length > 0,
      setDocument,
      resetPreviewRuntime,
      refreshDocumentReference,
      selectNode,
      addNode,
      updateNode,
      updateNodeDebounced,
      undo,
      redo,
      beginUndoGesture,
      endUndoGesture,
      prepareDebouncedUndo,
      scheduleDebouncedUndoCommit,
      deleteNode,
      deleteNodes,
      moveNode,
      moveNodesByDelta,
      resizeNode,
      rotateNode,
      bringForward,
      sendBackward,
      bringForwardOne,
      sendBackwardOne,
      duplicateNode,
      copySelectedNodesToPayload,
      cutSelectedNodesToPayload,
      pasteLayoutPayload,
      setMockOverrides,
      updateMockField,
      triggerAnimationPreview,
      groupSelectedNodes,
      ungroupNode,
      extractNodeFromContainer,
      reorderNodeInTree,
    }),
    [
      document,
      selectedNodeId,
      selectedNodeIds,
      previewData,
      mockOverrides,
      animationPreviewTicks,
      runtimeInvalidation,
      historyEpoch,
      setDocument,
      resetPreviewRuntime,
      refreshDocumentReference,
      selectNode,
      addNode,
      updateNode,
      updateNodeDebounced,
      undo,
      redo,
      beginUndoGesture,
      endUndoGesture,
      prepareDebouncedUndo,
      scheduleDebouncedUndoCommit,
      deleteNode,
      deleteNodes,
      moveNode,
      moveNodesByDelta,
      resizeNode,
      rotateNode,
      bringForward,
      sendBackward,
      bringForwardOne,
      sendBackwardOne,
      duplicateNode,
      copySelectedNodesToPayload,
      cutSelectedNodesToPayload,
      pasteLayoutPayload,
      setMockOverrides,
      updateMockField,
      triggerAnimationPreview,
      groupSelectedNodes,
      ungroupNode,
      extractNodeFromContainer,
      reorderNodeInTree,
    ],
  );

  return <EditorCtx.Provider value={value}>{children}</EditorCtx.Provider>;
};
